import React, { useContext, useCallback, useState, useEffect } from "react";
import { Link } from "gatsby";
import { useAsyncFn } from "react-use";
import { debounce } from "lodash";

// Context
import { StoreContext } from "../context/store-context";

// Icons
import { ArrowRight, ArrowLeft } from "../icons/cart-icons";

// Utils
import { formatPrice } from "../utils/format-price";

export const LineItem = ({ item }) => {
  const { removeLineItem, checkout, updateLineItem } = useContext(StoreContext);
  const [quantity, setQuantity] = useState(item.quantity);
  const [isOnePerCustomer, setIsOnePerCustomer] = useState(false);

  const subtotal = formatPrice(
    item.variant.priceV2.currencyCode,
    Number(item.variant.priceV2.amount) * quantity
  );

  const handleRemove = () => {
    removeLineItem(checkout.id, item.id);
  };

  const uli = debounce(
    value => updateLineItem(checkout.id, item.id, value),
    300
  );
  // eslint-disable-next-line
  const debouncedUli = useCallback(value => uli(value), []);

  const handleQuantityChange = value => {
    if (value !== "" && Number(value) < 1) {
      return;
    }
    if (isOnePerCustomer && Number(value) > 1) {
      return;
    }
    setQuantity(value);
    if (Number(value) >= 1) {
      debouncedUli(value);
    }
  };

  function doIncrement() {
    if (isOnePerCustomer && quantity >= 1) {
      return;
    }
    handleQuantityChange(Number(quantity || 0) + 1);
  }

  function doDecrement() {
    handleQuantityChange(Number(quantity || 0) - 1);
  }

  const [productMetafields, getProductMetafields] = useAsyncFn(async id => {
    const response = await fetch(`/.netlify/functions/get-product-artist`, {
      method: "POST",
      body: JSON.stringify({
        id: id,
      }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });

    const result = await response.json();
    return result;
  }, []);

  useEffect(() => {
    if (item.variant.id !== null) {
      getProductMetafields(item.variant.id);
    }
  }, [item]);

  const [productArtistName, setProductArtistName] = useState(``);

  useEffect(() => {
    if (
      productMetafields.loading === false &&
      productMetafields.value !== undefined
    ) {
      if (
        productMetafields.value.metafields !== null &&
        productMetafields.value.metafields !== undefined
      ) {
        if (productMetafields.value.metafields.length >= 1) {
          const artistName = productMetafields.value.metafields.filter(
            field => field.key === `artist_name`
          );
          const purchaseLimit = productMetafields.value.metafields.filter(
            field => field.key === `purchase_limit`
          );

          // Add safety check for artistName
          if (artistName && artistName.length > 0) {
            setProductArtistName(artistName[0].value);
          }

          if (purchaseLimit !== undefined && purchaseLimit !== "undefined") {
            if (purchaseLimit.length > 0) {
              if (purchaseLimit[0].value !== undefined) {
                setIsOnePerCustomer(purchaseLimit[0].value);

                // If one per customer and quantity is greater than 1, set to 1
                if (purchaseLimit[0].value && quantity > 1) {
                  handleQuantityChange(1);
                }
              }
            }
          } else {
            setIsOnePerCustomer(false);
          }
        }
      }
    }
  }, [productMetafields]);

  return (
    <div className="row item">
      <div className="item-image">
        <img src={item.variant.image.src} alt={item.title} loading="lazy" />
      </div>

      <div className="item-details">
        <h1 className="item-title">
          <Link to={`/products/${item.variant.product.handle}/`}>
            {productArtistName}
            <br />
            {item.title}
          </Link>
        </h1>
        <p className="item-price">{subtotal}</p>
        {item.variant.selectedOptions[0].name !== "Title" && (
          <p className="item-style">
            {item.variant.selectedOptions[0].name}:{" "}
            {item.variant.selectedOptions[0].value}
          </p>
        )}

        <div className="item-quantity">
          <div className="quantity-title">
            <p>Quantity</p>
          </div>

          <div className="quantity-buttons-container">
            {!isOnePerCustomer && (
              <button onClick={() => doDecrement()}>
                <ArrowLeft />
              </button>
            )}
            <span className="quantity">{item.quantity}</span>

            {!isOnePerCustomer && (
              <button
                onClick={() => doIncrement()}
                disabled={isOnePerCustomer && quantity >= 1}
              >
                <ArrowRight />
              </button>
            )}
          </div>
        </div>

        <div className="item-remove">
          <button onClick={handleRemove}>Remove</button>
        </div>
      </div>
    </div>
  );
};
